import { Separator } from '../../../components/separator'
import { useAppSelector } from '../../../redux/hook';
import { ApplicationStep3 } from '../../../models/classes/ApplicationStep3.class';
import { proposedHousingTypeItems } from '../../../utils/shared-dropdown';
import { HoldingTypeTypeCode } from '../../../utils/code-converter';
import useIsMobile from '../../../hooks/use-is-mobile';
import MobileTableMobile, { MobileTableItemInterface } from '../../../components/mobile/mobile-table';
import { useMemo } from 'react';
import { convertRoleToStructureStatus } from '../../../utils/form-helper';
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum';
import { MyInfoWithDOD } from '../../../models/polymer/MyInfo.model';

interface HoldingTypeTableProps{
  ConveyancingValue: ApplicationStep3;
}


function HoldingTypeTable({...props}:HoldingTypeTableProps) {
  const { ConveyancingValue } = props;
  
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const isSoleOwnership = applicationStep1FormData.household.incoming_owners.length === 1;
  
  const { isMobile } = useIsMobile();

  const hasExecutorAdministrator = useMemo(()=>{
    return applicationStartFormData.selected_hdb_property?.additional_roles.some(e=>{
      const roleStructureStatus = convertRoleToStructureStatus(e.role_name);
      return roleStructureStatus === StructureStatusEnum.executor || roleStructureStatus === StructureStatusEnum.administrator
    })
  },[applicationStartFormData.selected_hdb_property?.additional_roles])

  const executorAdministratorListing:MyInfoWithDOD[] = useMemo(()=>{
    let dataList:MyInfoWithDOD[] = [];
    if(hasExecutorAdministrator){
      applicationStartFormData.selected_hdb_property?.additional_roles.forEach(e=>{
        const roleStructureStatus = convertRoleToStructureStatus(e.role_name);
        if(roleStructureStatus === StructureStatusEnum.executor || roleStructureStatus === StructureStatusEnum.administrator){
          dataList.push(e);
        }
      })
    }
    return dataList;
  },[applicationStartFormData.selected_hdb_property?.additional_roles, hasExecutorAdministrator, applicationStartFormData.selected_hdb_property?.additional_roles])

  const current_mobile_table_items = useMemo(()=>{
    let obj_items:MobileTableItemInterface[] = [
      { header:"Owners", contents: applicationStep1FormData.household?.owners.map(e=>e.myinfo.name), placeholder:"You do not have any owner(s)" },
      { header:"Holding Type", contents: [HoldingTypeTypeCode.find(e=>e.code === applicationStartFormData.selected_hdb_property?.holding_type)?.description ?? "-"],  placeholder:"" },
    ];
    if(hasExecutorAdministrator && applicationStartFormData.selected_hdb_property?.additional_roles){
      obj_items.push({ header:"Executor/Administrator", contents: applicationStartFormData.selected_hdb_property?.additional_roles.map(additional_role => `${additional_role.name} (${additional_role.role_name})`),  placeholder:"" });
    }
    return obj_items;
  },[applicationStartFormData.selected_hdb_property?.holding_type, applicationStep1FormData.household?.owners, hasExecutorAdministrator, applicationStartFormData.selected_hdb_property?.additional_roles])

 
  const proposed_mobile_table_items = useMemo(()=>{
    let obj_items:MobileTableItemInterface[] = [
      { header:"Owners", contents: applicationStep1FormData.household.incoming_owners.map(e=>e.myinfo.name), placeholder:"You have not added proposed owner(s)" },
      { header:"Holding Type", contents: isSoleOwnership ? ['Sole Ownership'] : [proposedHousingTypeItems.find(e => e.value === ConveyancingValue.holding_type)?.title ?? ""],  placeholder:"You have not specified holding type" },
    ];
    if(hasExecutorAdministrator && applicationStartFormData.selected_hdb_property?.additional_roles){
      obj_items.push({ header:"Executor/Administrator", contents: applicationStartFormData.selected_hdb_property?.additional_roles.map(additional_role => `${additional_role.name} (${additional_role.role_name})`),  placeholder:"" });
    }
    return obj_items;
  },[ConveyancingValue.holding_type, applicationStep1FormData.household.incoming_owners, isSoleOwnership, hasExecutorAdministrator, applicationStartFormData.selected_hdb_property?.additional_roles])



  if(isMobile){
    return <>
      <MobileTableMobile title='Current household' items={current_mobile_table_items}/>
      <div style={{height:8}}></div>
      <MobileTableMobile title='Proposed household' items={proposed_mobile_table_items}/>
    </>
  }

  return (
    <div className='ohc-household-structure-table'>
      <div className='ohc-household-structure-table-header'>
        <div className='ohc-household-structure-table-column-1' />
        <div className='ohc-household-structure-table-column-2'>
          <p>Current household</p>
        </div>
        <div className='ohc-household-structure-table-column-2'>
          <p>Proposed household</p>
        </div>
      </div>
      <Separator style={{ margin: "16px 0" }} variant="solid" />
      <div className='ohc-household-structure-table-content'>
        <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header'>
          <p>Owners</p>
        </div>

        <div className='ohc-household-structure-table-column-2'>
          {
            applicationStep1FormData.household?.owners.length > 0
            ?(
              applicationStep1FormData.household?.owners?.map((owner, index) => {
                const key = index;
                return <p key={key}>{owner?.myinfo?.name}</p>
              })
            )
            : (
              <p>-</p>
            )
          }
        </div>

        <div className='ohc-household-structure-table-column-2'>
          {
            applicationStep1FormData.household.incoming_owners?.map((owner, index) => {
              const key = index;
              return <p key={key}>{owner.myinfo?.name}</p>
            })
          }
        </div>

      </div>
      {hasExecutorAdministrator && <>
            <Separator style={{margin:"16px 0"}}/>
            <div className='ohc-household-structure-table-content'>
              <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header' style={{display: "block"}}>
                <p>Executor/</p>
                <p>Administrator</p>
              </div>
              <div className='ohc-household-structure-table-column-2'>
                { executorAdministratorListing.map((additional_role,__index)=>{
                    const key = "additional_role"+__index;
                    return <p key={key}>{additional_role.name} ({additional_role.role_name})</p>
                  })
                }
              </div>
            </div>
          </>}
      <Separator style={{ margin: "16px 0" }} />
      <div className='ohc-household-structure-table-content'>
        <div className='ohc-household-structure-table-column-1 ohc-household-structure-table-header'>
          <p>Holding Type</p>
        </div>

        <div className='ohc-household-structure-table-column-2'>
          <p>{HoldingTypeTypeCode.find(e=>e.code === applicationStartFormData.selected_hdb_property?.holding_type)?.description ?? "-"}</p>
        </div>

        <div className='ohc-household-structure-table-column-2'>
          {isSoleOwnership && <p>Sole Ownership</p>}
          {!isSoleOwnership && ConveyancingValue.is_holding_type_confirm && <p>{proposedHousingTypeItems.find(e => e.value === ConveyancingValue.holding_type)?.title}</p>}
          {!ConveyancingValue.is_holding_type_confirm && <p className='ohc-household-structure-table-placeholder'>You have not specified holding type</p>}
        </div>

      </div>
    </div>
  )
}

export default HoldingTypeTable